import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Search, Users, X, Pencil, Upload, Download, ChevronDown, FileSpreadsheet,
} from 'lucide-react';
import Select from 'react-select';
import useFetch from '../constants/useFetch';
import { API_BASE_URL } from '../constants';
import * as XLSX from 'xlsx';
import ImportStudentsModal from '../components/ImportStudentsModal';
import debounce from 'lodash.debounce'; // npm install lodash
import SkeletonTable from '../components/SkeletonTable';

const statusOptions = [
  { value: 'all', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'expelled', label: 'Expelled' },
  { value: 'graduated', label: 'Graduated' },
  { value: 'transfered', label: 'Transferred' },
];

const categoryOptions = [
  { value: 'all', label: 'All categories' },
  { value: 'junior', label: 'Junior' },
  { value: 'senior', label: 'Senior' },
];

const sortOptions = [
  { value: 'a-z', label: 'Name A–Z' },
  { value: 'z-a', label: 'Name Z–A' },
  { value: 'adm-asc', label: 'Admission ASC' },
  { value: 'adm-desc', label: 'Admission DESC' },
];

const perPageOptions = [10, 20, 50, 100, 200, 500, 1000];

const customSelectStyles = { /* your styles unchanged */ };

/* ---------------------- Bulk Message Modal (unchanged) ---------------------- */
/* ---------------------- Bulk message modal (unchanged) ---------------------- */
const BulkMessageModal = ({ isOpen, onClose, count, onSubmit }) => {
  const [viaEmail, setViaEmail] = useState(true);
  const [viaSms, setViaSms] = useState(false);
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!viaEmail && !viaSms) return setError('Select at least one channel (Email or SMS).');
    if (viaEmail && !subject.trim()) return setError('Subject is required for email.');
    if (!content.trim()) return setError('Message content is required.');
    try {
      setSending(true);
      await onSubmit({ viaEmail, viaSms, subject, content });
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to send message.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Send Message ({count} selected)
          </h3>
          <button onClick={onClose} className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && <div className="text-sm text-red-600">{error}</div>}
          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input type="checkbox" checked={viaEmail} onChange={(e) => setViaEmail(e.target.checked)} />
              Email
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input type="checkbox" checked={viaSms} onChange={(e) => setViaSms(e.target.checked)} />
              SMS
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Title / Subject (for Email)</label>
            <input
              type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g. PTA Meeting Reminder"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Message</label>
            <textarea
              value={content} onChange={(e) => setContent(e.target.value)} rows={5} placeholder="Type your message…"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
              Cancel
            </button>
            <button type="submit" disabled={sending} className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50">
              {sending ? 'Sending…' : 'Send'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ---------------------------------- Page ---------------------------------- */
const AllStudents = () => {
  const { user: userInfo } = useSelector((s) => s.auth);
  const navigate = useNavigate();
  const location = useLocation();

  // === URL Sync State ===
  const params = new URLSearchParams(location.search);
  const [rawSearch, setRawSearch] = useState(params.get('search') || '');
  const [search, setSearch] = useState(rawSearch);
  const [status, setStatus] = useState(params.get('status') || 'all');
  const [category, setCategory] = useState(params.get('category') || 'all');
  const [sort, setSort] = useState(params.get('sort') || 'a-z');
  const [limit, setLimit] = useState(Number(params.get('limit')) || 50);
  const [page, setPage] = useState(Number(params.get('page')) || 1);

  const [selectedUuids, setSelectedUuids] = useState([]);
  const [imageUrl, setImageUrl] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [showMsgModal, setShowMsgModal] = useState(false);

  // === Debounced search ===
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSetSearch = useCallback(
    debounce((value) => setSearch(value), 300),
    []
  );

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setRawSearch(val);
    debouncedSetSearch(val);
    setPage(1);
  };

  // === Fetch ALL students once (huge limit) ===
  const url = `${API_BASE_URL}/api/web/admin/all-students?limit=10000`;
  const { data, loading, error, refetch } = useFetch(url, 'allStudentsFull');

  // === Deduped master list ===
  const allStudents = useMemo(() => {
    const arr = data?.students || [];
    const seen = new Set();
    const unique = [];
    for (const s of arr) {
      const key = s.uuid || `id:${s.id}`;
      if (!seen.has(key)) {
        seen.add(key);
        unique.push(s);
      }
    }
    return unique;
  }, [data?.students]);

  // === Client-side filtering & sorting ===
  const filteredAndSorted = useMemo(() => {
    let list = [...allStudents];

    // Search
    if (search.trim()) {
      const term = search.toLowerCase();
      list = list.filter((s) => {
        return (
          `${s.firstname} ${s.lastname} ${s.middlename}`.toLowerCase().includes(term) ||
          (s.admission_no || '').toLowerCase().includes(term) ||
          (s.student_email || '').toLowerCase().includes(term) ||
          (s.student_phone || '').toLowerCase().includes(term)
        );
      });
    }

    // Filters
    if (status !== 'all') list = list.filter((s) => s.status === status);
    if (category !== 'all') list = list.filter((s) => s.category === category);

    // Sorting
    const parseAdmission = (no = '') => {
      const parts = no.split('/');
      if (parts.length !== 3) return { num: 0, year: 0 };
      return { num: parseInt(parts[1], 10) || 0, year: parseInt(parts[2], 10) || 0 };
    };

    if (sort === 'a-z') {
      list.sort((a, b) => `${a.lastname} ${a.firstname}`.localeCompare(`${b.lastname} ${b.firstname}`));
    } else if (sort === 'z-a') {
      list.sort((a, b) => `${b.lastname} ${b.firstname}`.localeCompare(`${a.lastname} ${a.firstname}`));
    } else if (sort === 'adm-asc') {
      list.sort((a, b) => {
        const A = parseAdmission(a.admission_no);
        const B = parseAdmission(b.admission_no);
        return A.year - B.year || A.num - B.num;
      });
    } else if (sort === 'adm-desc') {
      list.sort((a, b) => {
        const A = parseAdmission(a.admission_no);
        const B = parseAdmission(b.admission_no);
        return B.year - A.year || B.num - A.num;
      });
    }

    return list;
  }, [allStudents, search, status, category, sort]);

  // === Client-side pagination ===
  const totalItems = filteredAndSorted.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / limit));

  const paginatedStudents = useMemo(() => {
    const start = (page - 1) * limit;
    return filteredAndSorted.slice(start, start + limit);
  }, [filteredAndSorted, page, limit]);

  // Ensure page is valid after filtering
  useEffect(() => {
    if (page > totalPages && totalPages > 0) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  // === URL Sync (only what we care about) ===
  useEffect(() => {
    const qs = new URLSearchParams();
    if (rawSearch) qs.set('search', rawSearch);
    if (status !== 'all') qs.set('status', status);
    if (category !== 'all') qs.set('category', category);
    if (sort !== 'a-z') qs.set('sort', sort);
    if (limit !== 50) qs.set('limit', String(limit));
    if (page !== 1) qs.set('page', String(page));

    const newUrl = qs.toString() ? `?${qs}` : location.pathname;
    if (`${location.pathname}${location.search}` !== newUrl) {
      navigate(newUrl, { replace: true });
    }
  }, [rawSearch, status, category, sort, limit, page, navigate, location.pathname, location.search]);

  // === Selection logic (current page) ===
  const uuidsOnPage = useMemo(() => paginatedStudents.map((s) => s.uuid).filter(Boolean), [paginatedStudents]);

  const allChecked = uuidsOnPage.length > 0 && uuidsOnPage.every((u) => selectedUuids.includes(u));

  const toggleSelectAll = () => {
    if (allChecked) {
      setSelectedUuids((prev) => prev.filter((u) => !uuidsOnPage.includes(u)));
    } else {
      setSelectedUuids((prev) => Array.from(new Set([...prev, ...uuidsOnPage])));
    }
  };

  const toggleSelectOne = (uuid) => {
    setSelectedUuids((prev) =>
      prev.includes(uuid) ? prev.filter((u) => u !== uuid) : [...prev, uuid]
    );
  };

  const clearSelection = () => setSelectedUuids([]);

  // === Helpers ===
  const formatDate = (d) => (!d ? 'N/A' : new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }));

  const parseAdmission = (no = '') => {
    const parts = no.split('/');
    if (parts.length !== 3) return { num: 0, year: 0 };
    return { num: parseInt(parts[1], 10) || 0, year: parseInt(parts[2], 10) || 0 };
  };

  // === Export (uses filtered list directly) ===
  const shapeForExport = (list) =>
    list.map((s) => ({
      uuid: s.uuid || '',
      admission_no: s.admission_no || '',
      lastname: s.lastname || '',
      firstname: s.firstname || '',
      middlename: s.middlename || '',
      gender: s.gender || '',
      date_of_birth: s.date_of_birth || '',
      student_email: s.student_email || '',
      student_phone: s.student_phone || '',
      admission_date: s.admission_date || '',
      classroom_name: s.classroom_name || '',
      status: s.status || '',
      post: s.post || '',
      class_post: s.class_post || '',
    }));

  const exportToXlsx = (rows, filename) => {
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Students');
    XLSX.writeFile(wb, filename);
  };

  const handleExport = (format, scope) => {
    const list = scope === 'selected'
      ? allStudents.filter((s) => selectedUuids.includes(s.uuid))
      : filteredAndSorted;

    const rows = shapeForExport(list);
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    const filename = `students-${scope}-${stamp}.${format === 'xlsx' ? 'xlsx' : 'csv'}`;

    if (format === 'xlsx') {
      exportToXlsx(rows, filename);
    } else {
      const ws = XLSX.utils.json_to_sheet(rows);
      const csv = XLSX.utils.sheet_to_csv(ws);
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    }
    setExportOpen(false);
  };

  // === Select options ===
  const statusSelectValue = statusOptions.find((o) => o.value === status) ?? statusOptions[0];
  const categorySelectValue = categoryOptions.find((o) => o.value === category) ?? categoryOptions[0];
  const sortSelectValue = sortOptions.find((o) => o.value === sort) ?? sortOptions[0];

  return (
    <div className="space-y-6 p-6 bg-gray-100 dark:bg-gray-900 min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl p-6 text-white">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">All Students</h1>
            <div className="flex items-center gap-2 text-blue-100 mt-2">
              <Users className="w-5 h-5" />
              <span>{totalItems.toLocaleString()} total (filtered)</span>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => setImportOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg"
            >
              <Upload className="w-4 h-4" />
              Import
            </button>

            <div className="relative">
              <button
                onClick={() => setExportOpen((v) => !v)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg"
              >
                <Download className="w-4 h-4" />
                Export
                <ChevronDown className="w-4 h-4" />
              </button>
              {exportOpen && (
                <div className="absolute right-0 mt-2 z-50 w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-2">
                  <div className="px-2 py-1 text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">XLSX</div>
                  <button
                    onClick={() => handleExport('xlsx', 'selected')}
                    disabled={selectedUuids.length === 0}
                    className="w-full text-left px-3 py-2 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                  >
                    Selected ({selectedUuids.length})
                  </button>
                  <button
                    onClick={() => handleExport('xlsx', 'all')}
                    className="w-full text-left px-3 py-2 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    All (filtered)
                  </button>

                  <div className="px-2 pt-3 pb-1 text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700 mt-2">CSV</div>
                  <button
                    onClick={() => handleExport('csv', 'selected')}
                    disabled={selectedUuids.length === 0}
                    className="w-full text-left px-3 py-2 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                  >
                    Selected ({selectedUuids.length})
                  </button>
                  <button
                    onClick={() => handleExport('csv', 'all')}
                    className="w-full text-left px-3 py-2 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    All (filtered)
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 border">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3">
          <div className="relative col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search name, admission no., email…"
              value={rawSearch}
              onChange={handleSearchChange}
              className="w-full pl-10 pr-4 py-2 border rounded-lg dark:bg-gray-700"
            />
          </div>
          <Select value={statusSelectValue} onChange={(v) => { setStatus(v.value); setPage(1); }} options={statusOptions} styles={customSelectStyles} />
          <Select value={categorySelectValue} onChange={(v) => { setCategory(v.value); setPage(1); }} options={categoryOptions} styles={customSelectStyles} />
          <Select value={sortSelectValue} onChange={(v) => { setSort(v.value); setPage(1); }} options={sortOptions} styles={customSelectStyles} />
          <select
            value={limit}
            onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
            className="px-3 py-2 border rounded-lg dark:bg-gray-700"
          >
            {perPageOptions.map((n) => (
              <option key={n} value={n}>{n} per page</option>
            ))}
          </select>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedUuids.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 flex justify-between items-center">
          <span>{selectedUuids.length} selected</span>
          <div className="flex gap-2">
            <button onClick={() => setShowMsgModal(true)} className="px-4 py-2 bg-blue-600 text-white rounded-lg">
              Send Message
            </button>
            <button onClick={clearSelection} className="px-4 py-2 border rounded-lg">
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3">
                <input
                  type="checkbox"
                  checked={allChecked}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Student</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Admission No.</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Class</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Admission Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date of Birth</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">School Post</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Class Post</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
            <tbody>
              {loading ? 
                <>
                  <SkeletonTable rows={8} columns={10} />
                </>
            //   Array.from({ length: 8 }).map((_, i) => (
                
            //   ))
             : paginatedStudents.length === 0 ? (
                <tr><td colSpan={10} className="text-center py-12 text-gray-500">No students found</td></tr>
              ) : (
                paginatedStudents.map((s) => (
                  <tr key={s.uuid || s.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedUuids.includes(s.uuid)}
                        onChange={() => toggleSelectOne(s.uuid)}
                      />
                    </td>
                    {/* Student (avatar + name) */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          {s.profile_picture ? (
                            <img
                              src={s.profile_picture}
                              alt={`${s.firstname || ''} ${s.lastname || ''}`}
                              className="w-10 h-10 rounded-full object-cover cursor-zoom-in"
                              onClick={() => setImageUrl(s.profile_picture)}
                            />
                          ) : (
                            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                              {(s.firstname?.[0] || '').toUpperCase()}{(s.lastname?.[0] || '').toUpperCase()}
                            </div>
                          )}
                        </div>
                        <div>
                          <button
                            type="button"
                            onClick={() => navigate(`/student/${s.uuid}`)}
                            className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
                            title="View student details"
                          >
                            {s.lastname} {s.firstname} {s.middlename}
                          </button>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {s.student_email || '—'}
                          </div>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{s.admission_no || '—'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{s.classroom_name || '—'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{formatDate(s.admission_date)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{formatDate(s.date_of_birth)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{s.post || '—'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{s.class_post || '—'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        s.status === 'active'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                        }`}>
                        {s.status || 'active'}
                        </span>
                    </td>

                    {/* Actions */}
                    <td className="px-6 py-4 whitespace-nowrap">
                        <button
                        className="px-2 py-1 text-sm text-gray-700 dark:text-gray-300 hover:underline"
                        onClick={() => navigate(`/edit-student/${s.uuid}`)}
                        title="Edit student"
                        >
                        <Pencil className="w-4 h-4 inline-block mr-1" />
                        Edit
                        </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex justify-between items-center px-6 py-4 border-t">
          <div className="text-sm text-gray-600">
            Showing {(page - 1) * limit + 1}–{Math.min(page * limit, totalItems)} of {totalItems}
          </div>
          <div className="flex gap-2">
            <button disabled={page === 1} onClick={() => setPage(p => p - 1)}>Prev</button>
            <span className="px-3">{page} / {totalPages}</span>
            <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
          </div>
        </div>
      </div>

      {/* Modals */}
      <BulkMessageModal isOpen={showMsgModal} 
        onClose={() => { setShowMsgModal(false); clearSelection(); }} 
        count={selectedUuids.length} 
        onSubmit={async (data) => {
            const endpoint = `${API_BASE_URL}/api/web/admin/students/bulk-message`;
            const payload = {
                studentUuids: selectedUuids,
                via: { email: !!data.viaEmail, sms: !!data.viaSms },
                subject: data.subject,
                content: data.content,
            };
            const resp = await fetch(endpoint, {
                method: 'POST',
                headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${userInfo?.token || localStorage.getItem('authToken')}`,
                },
                body: JSON.stringify(payload),
            });
            const json = await resp.json();
            if (!resp.ok || !json?.success) throw new Error(json?.message || 'Failed to send.');
            alert('Message queued/sent successfully.');
            clearSelection();
        }} 
      />
      <ImportStudentsModal
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={refetch}
        apiBaseUrl={API_BASE_URL}
        authToken={userInfo?.token}
      />

      {imageUrl && 
        <ImportStudentsModal
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => {
            // re-fetch your table
            if (typeof refetch === 'function') refetch();
            setImportOpen(false);
        }}
        apiBaseUrl={API_BASE_URL}
        authToken={userInfo?.token}
        />
      }
    </div>
  );
};

export default AllStudents;